import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { filterSuggestionItems } from '@blocknote/core';
import {
  SuggestionMenuController,
  useComponentsContext,
  useCreateBlockNote,
  useDictionary,
  useEditorChange,
} from '@blocknote/react';
import type { DefaultReactSuggestionItem, SuggestionMenuProps } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import { useTranslation } from 'react-i18next';

import {
  sharedEditorSchema,
  SharedEditorInstance,
} from '@/components/atoms/BlockNoteEditor/schema';
import { getVariableSuggestionItems } from '@/components/atoms/BlockNoteEditor/variableUtils';
import {
  buildContextVariableLookup,
  createMediaStrippingPasteHandler,
  normalizeBlocksWithContextVariables,
  parseMarkdownToBlocks,
  trimMarkdown,
} from '@/components/atoms/Editors/BlockNoteEditor/editorUtils';
import { Option } from '@/shared/types/rules';

import './CampaignEditor.scss';

/**
 * Re-export schema and type for backwards compatibility
 */
export const campaignEditorSchema = sharedEditorSchema;
export type CampaignEditorInstance = SharedEditorInstance;

/**
 * Re-export utilities for backwards compatibility
 */
export {
  normalizeBlocksWithContextVariables,
  sanitizeContextToken,
} from '@/components/atoms/Editors/BlockNoteEditor/editorUtils';

interface CampaignEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  variableOptions: Option[];
  placeholder?: string;
  className?: string;
  maxLength?: number;
  emptySuggestionText?: string;
  editable?: boolean;
  /**
   * When true, the placeholder is shown only while the editor is completely
   * empty — not on every new empty line created by Enter. Defaults to false,
   * which keeps BlockNote's per-focused-block placeholder behavior.
   */
  placeholderWhenEmptyOnly?: boolean;
}

const CustomSuggestionMenu = ({
  items,
  loadingState,
  selectedIndex,
  onItemClick,
  emptyText,
}: SuggestionMenuProps<DefaultReactSuggestionItem> & { emptyText?: string }) => {
  const Components = useComponentsContext()!;
  const dict = useDictionary();

  const loader =
    loadingState === 'loading-initial' || loadingState === 'loading' ? (
      <Components.SuggestionMenu.Loader className="bn-suggestion-menu-loader" />
    ) : null;

  const renderedItems = useMemo(() => {
    let lastGroup: string | undefined;
    const result: ReactNode[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.group !== lastGroup) {
        lastGroup = item.group;
        result.push(
          <Components.SuggestionMenu.Label key={lastGroup} className="bn-suggestion-menu-label">
            {lastGroup}
          </Components.SuggestionMenu.Label>
        );
      }
      result.push(
        <Components.SuggestionMenu.Item
          key={item.title}
          className={`bn-suggestion-menu-item${item.size === 'small' ? 'bn-suggestion-menu-item-small' : ''}`}
          item={item}
          id={`bn-suggestion-menu-item-${i}`}
          isSelected={i === selectedIndex}
          onClick={() => onItemClick?.(item)}
        />
      );
    }
    return result;
  }, [Components, items, onItemClick, selectedIndex]);

  return (
    <Components.SuggestionMenu.Root id="bn-suggestion-menu" className="bn-suggestion-menu">
      {renderedItems}
      {renderedItems.length === 0 && (loadingState === 'loading' || loadingState === 'loaded') && (
        <Components.SuggestionMenu.EmptyItem className="bn-suggestion-menu-item">
          {emptyText || dict.suggestion_menu.no_items_title}
        </Components.SuggestionMenu.EmptyItem>
      )}
      {loader}
    </Components.SuggestionMenu.Root>
  );
};

/**
 * Campaign-specific BlockNote editor with variable highlighting and $ autocomplete
 * @param content - Current markdown content
 * @param onContentChange - Callback when content changes
 * @param variableOptions - Available context variables for autocomplete
 * @param placeholder - Placeholder text for empty editor
 * @param className - Additional CSS classes
 * @param maxLength - Optional maximum character length (enforced if provided)
 * @param emptySuggestionText - Custom text shown when no suggestion items match
 */
export function CampaignEditor({
  content,
  onContentChange,
  variableOptions,
  placeholder = '',
  className = '',
  maxLength,
  emptySuggestionText,
  editable = true,
  placeholderWhenEmptyOnly = false,
}: CampaignEditorProps) {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  const lastInitializedContentRef = useRef<string | null>(null);

  // Create editor instance with custom schema.
  // `emptyDocument` shows the placeholder only when the whole editor is empty;
  // `default` shows it on every focused empty block (incl. new lines after Enter).
  const editor = useCreateBlockNote({
    placeholders: placeholderWhenEmptyOnly
      ? { emptyDocument: placeholder, default: '' }
      : { default: placeholder },
    schema: campaignEditorSchema,
    pasteHandler: createMediaStrippingPasteHandler(),
  });

  // Precompute context variable lookup for efficient normalization
  const contextVariableLookup = useMemo(
    () => buildContextVariableLookup(variableOptions),
    [variableOptions]
  );

  // Function to load and normalize content
  const loadAndNormalizeContent = useCallback(
    async (currentContent: string = content) => {
      try {
        const blocks = await parseMarkdownToBlocks(editor, currentContent || '');

        // Normalize any existing variables in the loaded content
        if (blocks.length > 0 && contextVariableLookup.size > 0) {
          const { normalized } = normalizeBlocksWithContextVariables(blocks, contextVariableLookup);
          editor.replaceBlocks(editor.document, normalized as typeof editor.document);
        } else {
          editor.replaceBlocks(editor.document, blocks as typeof editor.document);
        }
      } catch (error) {
        console.error('Failed to parse markdown:', error);
      }
    },
    [editor, content, contextVariableLookup]
  );

  // Initialize editor content from markdown and normalize variables
  useEffect(() => {
    // Re-initialize if:
    // 1. Never initialized before
    // 2. Content changed externally (different from what we last initialized with)
    // This handles switching between channels or loading data from API
    const contentChanged = lastInitializedContentRef.current !== content;
    const shouldInitialize = !isInitialized || contentChanged;

    if (!shouldInitialize) {
      return;
    }

    const initializeEditor = async () => {
      await loadAndNormalizeContent(content);
      lastInitializedContentRef.current = content;
      setIsInitialized(true);
    };

    initializeEditor();
  }, [editor, content, loadAndNormalizeContent, isInitialized]);

  // Custom suggestion menu with overridden empty text
  const suggestionMenuComponent = useMemo(() => {
    if (!emptySuggestionText) {
      return undefined;
    }
    const MenuWithCustomEmpty = (props: SuggestionMenuProps<DefaultReactSuggestionItem>) => (
      <CustomSuggestionMenu {...props} emptyText={emptySuggestionText} />
    );
    return MenuWithCustomEmpty;
  }, [emptySuggestionText]);

  // Convert variable options to suggestion items
  const variableSuggestionItems = useMemo(
    () => getVariableSuggestionItems(editor, variableOptions, t),
    [editor, variableOptions, t]
  );

  // Handle editor changes and normalize variables
  useEditorChange(
    useCallback(
      async (editor) => {
        if (!isInitialized) {
          return;
        }

        try {
          // Normalize any typed variable patterns
          const currentBlocks = editor.document;
          const { normalized, changed } = normalizeBlocksWithContextVariables(
            currentBlocks,
            contextVariableLookup
          );

          // Update editor if normalization made changes
          if (changed) {
            // Save cursor position before replacing blocks
            const cursorPos = editor.getTextCursorPosition();
            const currentBlockIndex = cursorPos?.block
              ? currentBlocks.findIndex((b) => b.id === cursorPos.block.id)
              : -1;

            editor.replaceBlocks(editor.document, normalized);

            // Restore cursor to end of the same block
            if (currentBlockIndex >= 0 && currentBlockIndex < normalized.length) {
              editor.setTextCursorPosition(normalized[currentBlockIndex], 'end');
            }

            // After normalization, propagate the content to parent
            // This ensures form state stays in sync even when normalization occurs
            let normalizedMarkdown = await editor.blocksToMarkdownLossy(normalized);
            normalizedMarkdown = trimMarkdown(normalizedMarkdown);

            // Check maxLength even for normalized content
            if (maxLength !== undefined && normalizedMarkdown.length > maxLength) {
              await loadAndNormalizeContent(content);
              return;
            }

            // Update the ref to prevent re-initialization when the content prop updates
            // due to this change (avoiding infinite loops)
            lastInitializedContentRef.current = normalizedMarkdown;
            onContentChange(normalizedMarkdown);
            return;
          }

          // Convert to markdown
          let markdown = await editor.blocksToMarkdownLossy(editor.document);
          markdown = trimMarkdown(markdown);

          // Enforce maxLength if provided
          if (maxLength !== undefined && markdown.length > maxLength) {
            // Revert to previous content by reloading
            await loadAndNormalizeContent(content);
            return;
          }

          // Update the ref to prevent re-initialization when the content prop updates
          // due to this change (avoiding infinite loops)
          lastInitializedContentRef.current = markdown;
          onContentChange(markdown);
        } catch (error) {
          console.error('Failed to process editor change:', error);
        }
      },
      [
        isInitialized,
        onContentChange,
        contextVariableLookup,
        maxLength,
        loadAndNormalizeContent,
        content,
      ]
    ),
    editor
  );
  return (
    <div className={`campaign-editor ${className}`.trim()}>
      <BlockNoteView
        theme="light"
        editor={editor}
        editable={editable}
        formattingToolbar={false}
        sideMenu={false}
        slashMenu={false}
        data-theming-css-blocknote
      >
        <SuggestionMenuController
          triggerCharacter="$"
          getItems={async (query) => filterSuggestionItems(variableSuggestionItems, query)}
          suggestionMenuComponent={suggestionMenuComponent}
        />
      </BlockNoteView>
    </div>
  );
}
